import React from 'react';
import { Link } from '@/components/LocalizedLink';
import { ArrowRight, Globe, Ship, FileCheck, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import CountryGuides from '@/components/CountryGuides';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const scaleIn = { hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } };

const REGIONS = [
  {
    name: 'United Arab Emirates',
    flag: 'ae',
    desc: 'One of our largest markets. Regular shipments to Dubai, Abu Dhabi, and Sharjah for spice traders and food processing units.',
    products: ['Turmeric Powder', 'Red Chilli Powder', 'Cumin Powder'],
    highlight: true,
  },
  {
    name: 'United Kingdom',
    flag: 'gb',
    desc: 'Supplying the UK food industry with premium Indian spices. Full compliance with UK import regulations.',
    products: ['Turmeric Powder', 'Coriander Powder', 'Cumin Powder'],
    highlight: true,
  },
  {
    name: 'Germany',
    flag: 'de',
    desc: 'Meeting EU quality standards for spice exports. Regular consignments to German distributors and food manufacturers.',
    products: ['Turmeric Powder', 'Garlic Powder', 'Red Chilli Powder'],
  },
  {
    name: 'Netherlands',
    flag: 'nl',
    desc: 'Rotterdam port hub for spice distribution across Europe. Established buyer network in the Netherlands.',
    products: ['Coriander Powder', 'Cumin Powder', 'Turmeric Powder'],
  },
  {
    name: 'Saudi Arabia',
    flag: 'sa',
    desc: 'Growing market for Indian spice exports. SFDA-compliant product packaging and labeling.',
    products: ['Red Chilli Powder', 'Cumin Powder', 'Garlic Powder'],
    highlight: true,
  },
  {
    name: 'Singapore',
    flag: 'sg',
    desc: 'Key Asian hub for spice re-export. High-quality cumin and coriander powder in demand.',
    products: ['Cumin Powder', 'Coriander Powder', 'Turmeric Powder'],
  },
  {
    name: 'Malaysia',
    flag: 'my',
    desc: 'Regular exports of turmeric, chilli and coriander powder for Malaysian food manufacturers and traders.',
    products: ['Turmeric Powder', 'Red Chilli Powder', 'Coriander Powder'],
  },
  {
    name: 'Sri Lanka',
    flag: 'lk',
    desc: 'Neighbouring market with strong demand for Indian-origin spices, especially turmeric and cumin powder.',
    products: ['Turmeric Powder', 'Cumin Powder', 'Red Chilli Powder'],
  },
];

const ALL_COUNTRIES = ['UAE', 'UK', 'Germany', 'Netherlands', 'Saudi Arabia', 'Singapore', 'Malaysia', 'Sri Lanka', 'Oman', 'Qatar', 'Bahrain', 'France', 'Belgium', 'Bangladesh', 'Nepal'];

export default function ExportMarketsPage() {
  return (
    <div data-testid="export-markets-page">

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-saffron-50/50 to-cream" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.7 }}>
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Global Presence</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-earth-dark mt-3 tracking-tight">
              Our Export <span className="text-saffron">Markets</span>
            </h1>
            <p className="mt-4 text-lg text-earth/60 font-sans max-w-xl leading-relaxed">
              We export premium Indian spices to 15+ countries across the Middle East, Europe, and Asia. Full regulatory compliance for each destination.
            </p>
          </motion.div>
        </div>
      </section>

      {/* World Map Visual */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="world-map-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={scaleIn} transition={{ duration: 0.7 }}
            className="bg-earth-dark rounded-xl p-8 sm:p-12 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10">
              <svg viewBox="0 0 1200 600" className="w-full h-full">
                <ellipse cx="600" cy="300" rx="550" ry="250" fill="none" stroke="#D4870A" strokeWidth="1" strokeDasharray="5,10" />
                <ellipse cx="600" cy="300" rx="400" ry="180" fill="none" stroke="#D4870A" strokeWidth="0.5" strokeDasharray="3,8" />
                <ellipse cx="600" cy="300" rx="250" ry="110" fill="none" stroke="#D4870A" strokeWidth="0.5" strokeDasharray="3,8" />
                <circle cx="720" cy="280" r="6" fill="#D4870A" />
                <circle cx="720" cy="280" r="12" fill="none" stroke="#D4870A" strokeWidth="1" opacity="0.5" />
                {[
                  [660, 260], [500, 200], [480, 210], [490, 180], [640, 250], [780, 270], [770, 290], [730, 310]
                ].map(([x, y]) => (
                  <g key={`${x}-${y}`}>
                    <line x1="720" y1="280" x2={x} y2={y} stroke="#D4870A" strokeWidth="0.5" strokeDasharray="3,5" opacity="0.4" />
                    <circle cx={x} cy={y} r="4" fill="#F5A623" />
                  </g>
                ))}
              </svg>
            </div>
            <div className="relative">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, type: "spring" }}
              >
                <Globe size={48} className="text-saffron mx-auto mb-4" />
              </motion.div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">15+ Countries</h2>
              <p className="text-cream-muted/60 font-sans mt-2">Across Middle East, Europe & Asia</p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {ALL_COUNTRIES.map((c, idx) => (
                  <motion.span
                    key={c}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + idx * 0.05 }}
                    className="text-[10px] uppercase tracking-wider text-saffron/70 border border-saffron/20 px-3 py-1 rounded-full font-sans bg-saffron/5 hover:bg-saffron/10 hover:text-saffron transition-colors cursor-default"
                  >
                    {c}
                  </motion.span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Markets Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Key Markets</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-earth-dark mt-3 tracking-tight">Active Export Destinations</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {REGIONS.map((region, i) => (
              <motion.div
                key={region.name}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className={`bg-white p-6 rounded-lg border shadow-card-lift hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 group ${
                  region.highlight ? 'border-saffron/30 ring-1 ring-saffron/10' : 'border-saffron-100/50'
                }`}
                data-testid={`market-${region.flag}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={`https://flagcdn.com/w80/${region.flag}.png`}
                    alt={region.name}
                    width={48}
                    height={32}
                    className="w-12 h-8 object-cover rounded shadow-sm"
                    loading="lazy"
                  />
                  {region.highlight && (
                    <span className="text-[8px] uppercase tracking-wider text-saffron font-sans font-bold bg-saffron/10 px-2 py-0.5 rounded">Top Market</span>
                  )}
                </div>
                <h3 className="font-display text-base font-bold text-earth-dark group-hover:text-saffron transition-colors">{region.name}</h3>
                <p className="mt-2 text-xs text-earth/50 font-sans leading-relaxed">{region.desc}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {region.products.map(p => (
                    <span key={p} className="text-[9px] uppercase tracking-wider text-saffron font-sans bg-saffron/5 px-2 py-0.5 rounded">{p}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CountryGuides />

      {/* Shipping & Logistics */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="shipping-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Logistics</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-earth-dark mt-3 tracking-tight">Shipping & Documentation</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Ship, title: 'Flexible Shipping', desc: 'FCL & LCL options. FOB, CIF, and CNF pricing available. Shipments from JNPT/Mundra ports.', color: '#D4870A' },
              { icon: FileCheck, title: 'Full Documentation', desc: 'Phytosanitary, Certificate of Origin, Fumigation, Health Certificate, Lab Reports — all handled by us.', color: '#6B8E23' },
              { icon: MapPin, title: 'Port to Door', desc: 'End-to-end logistics with trusted freight partners. Real-time tracking and estimated delivery timelines.', color: '#C0392B' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="bg-cream/50 p-6 rounded-lg border border-saffron-100/30 hover:shadow-card-lift transition-all"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: item.color + '15' }}>
                  <item.icon size={22} style={{ color: item.color }} />
                </div>
                <h3 className="font-display text-base font-bold text-earth-dark">{item.title}</h3>
                <p className="mt-2 text-xs text-earth/50 font-sans leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Import Compliance */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-saffron-50/30" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-earth-dark tracking-tight">Import Compliance Ready</h2>
            <p className="mt-2 text-earth/60 font-sans">We handle all documentation required for each market</p>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              'Phytosanitary Certificate',
              'Certificate of Origin',
              'Fumigation Certificate',
              'Health Certificate',
              'FSSAI License Copy',
              'Lab Analysis Report'
            ].map((doc, i) => (
              <motion.div
                key={doc}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white text-center p-4 rounded-lg border border-saffron-100/50 hover:border-saffron/30 hover:shadow-card-lift transition-all"
              >
                <FileCheck size={18} className="text-saffron mx-auto mb-2" />
                <p className="text-xs text-earth-dark font-sans font-medium">{doc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-saffron text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">Expanding into New Markets?</h2>
          <p className="mt-2 text-saffron-100 font-sans">We can customize documentation and packaging for any destination country.</p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-white text-saffron-dark font-sans font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-cream-warm transition-colors" data-testid="export-cta">
            Contact Export Team <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
